import styled from '@emotion/styled';
import { FileDownload } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, Grid, Tooltip, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { WorkSheet, WorkBook, utils, writeFile } from 'xlsx';
import { AddComponentType } from '../../constants/addComponentType';
import { useNavigate } from 'react-router-dom';
import AddCommonCode from '../add-common-code';
import AddDefaultLedger from '../add-default-ledger';
import AddStockMaster from '../add-stock-master';
import AddUserDialog from '../add-user-mapping';
import AddCompanyMaster from '../add-company-master';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const FileUpload = (props: {
  onFileUpload: (file: File, reset: () => void) => void;
  headers: string[];
  addComponentType: AddComponentType;
  showFileImport?: boolean;
}) => {
  const { showFileImport = true } = props;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onFileSelection = (e: any) => {
    setSelectedFile(e.target.files[0]);
  };

  const resetFileUpload = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onUploadClick = () => {
    if (selectedFile) {
      props.onFileUpload(selectedFile, resetFileUpload);
    }
  };

  useEffect(() => {
    return () => {
      setSelectedFile(null);
    };
  }, []);

  const downloadSampleFile = () => {
    const ws: WorkSheet = utils.aoa_to_sheet([props.headers]);
    const wb: WorkBook = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Sheet1');

    const fileName = () => {
      switch (props.addComponentType) {
        case AddComponentType.CommonCode:
          return 'vendor-master.xlsx';
        case AddComponentType.StockMaster:
          return 'stock-master.xlsx';
        case AddComponentType.DefaultLedger:
          return 'default-ledger.xlsx';
        case AddComponentType.User:
          return 'user-mapping.xlsx';
        case AddComponentType.CompanyMaster:
          return 'company-master.xlsx';
        case AddComponentType.ManageTenants:
          return 'manage-tenant.xlsx';
        default:
          return 'sample.xlsx';
      }
    };
    writeFile(wb, fileName());
  };

  const toolTipContent = (
    <div>
      <Typography>Download sample</Typography>
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center">
        <Button
          variant="contained"
          color="info"
          startIcon={<FileDownload />}
          onClick={downloadSampleFile}
          size="small">
          Sample
        </Button>
      </Grid>
    </div>
  );

  const handleAddNew = () => {
    if (props.addComponentType === AddComponentType.ManageTenants) {
      navigate('/super-admin/new-tenant');
    } else {
      setShowDialog(true);
    }
  };

  return (
    <div className="space-x-2">
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleAddNew}
        size="small"
        className="mr-2">
        Add New
      </Button>
      {showFileImport && (
        <>
          <Button
            component="label"
            variant="contained"
            startIcon={<FileUploadIcon />}
            sx={{
              marginRight: selectedFile ? 2 : 0,
              textTransform: 'none',
            }}
            disabled={!!selectedFile}
            size="small">
            {selectedFile
              ? (selectedFile as File)?.name
              : 'SELECT FILE TO IMPORT'}
            <VisuallyHiddenInput
              ref={fileInputRef}
              type="file"
              onChange={onFileSelection}
              accept=".xlsx"
            />
          </Button>
          {!selectedFile && (
            <Tooltip title={toolTipContent}>
              <InfoOutlinedIcon color="primary" fontSize="large" />
            </Tooltip>
          )}
        </>
      )}
      {selectedFile && (
        <Button
          variant="outlined"
          onClick={resetFileUpload}
          sx={{ marginRight: 2 }}
          size="small">
          Clear
        </Button>
      )}
      {selectedFile && (
        <Button variant="outlined" onClick={onUploadClick} size="small">
          Import
        </Button>
      )}

      {showDialog && (
        <>
          {props.addComponentType === AddComponentType.CommonCode && (
            <AddCommonCode
              showDialog={showDialog}
              setShowDialog={setShowDialog}
            />
          )}
          {props.addComponentType === AddComponentType.StockMaster && (
            <AddStockMaster
              showDialog={showDialog}
              setShowDialog={setShowDialog}
            />
          )}
          {props.addComponentType === AddComponentType.DefaultLedger && (
            <AddDefaultLedger
              showDialog={showDialog}
              setShowDialog={setShowDialog}
            />
          )}
          {props.addComponentType === AddComponentType.User && (
            <AddUserDialog
              showDialog={showDialog}
              setShowDialog={setShowDialog}
            />
          )}
          {props.addComponentType === AddComponentType.CompanyMaster && (
            <AddCompanyMaster
              showDialog={showDialog}
              setShowDialog={setShowDialog}
            />
          )}
        </>
      )}
    </div>
  );
};

export default FileUpload;
